import { api } from "@/lib/apiClient";

export type VehicleModelAdmin = {
  id: number;
  brand_id: number;
  brand_name?: string | null;
  name: string;
  slug: string;
  is_popular: boolean;
  sort_order: number;
  created_at?: string;
  updated_at?: string;
};

export type VehicleVersionAdmin = {
  id: number;
  vehicle_model_id: number;
  vehicle_model_name?: string | null;
  brand_id?: number | null;
  brand_name?: string | null;
  name: string;
  slug: string;
  is_popular: boolean;
  sort_order: number;
  start_year?: number | null;
  end_year?: number | null;
  is_imported?: boolean;
  created_at?: string;
  updated_at?: string;
};

function extractDataRows<T>(body: unknown): T[] {
  if (!body || typeof body !== "object") return [];
  const data = (body as Record<string, unknown>).data;
  return Array.isArray(data) ? (data as T[]) : [];
}

export type VehicleModelListMeta = {
  page: number;
  per_page: number;
  total: number;
  last_page: number;
};

export type VehicleVersionListMeta = VehicleModelListMeta;

/** One page of vehicle models (server-side pagination). */
export async function fetchVehicleModelsPage(params: {
  page?: number;
  per_page?: number;
  q?: string;
  brand_id?: string;
  name?: string;
  slug?: string;
  sort?: string;
  created_on?: string;
  created_from?: string;
  created_to?: string;
}): Promise<{ data: VehicleModelAdmin[]; meta: VehicleModelListMeta }> {
  const res = await api.get("/api/v1/catalog/vehicle-models", {
    params: {
      page: params.page ?? 1,
      per_page: params.per_page ?? 20,
      q: params.q?.trim() || undefined,
      brand_id: params.brand_id?.trim() || undefined,
      name: params.name?.trim() || undefined,
      slug: params.slug?.trim() || undefined,
      sort: params.sort?.trim() || undefined,
      created_on: params.created_on?.trim() || undefined,
      created_from: params.created_from?.trim() || undefined,
      created_to: params.created_to?.trim() || undefined,
    },
  });
  const body = res.data as Record<string, unknown>;
  const data = extractDataRows<VehicleModelAdmin>(body);
  const metaRaw = body.meta as Record<string, unknown> | undefined;
  const perPage = Math.max(1, Number(metaRaw?.per_page ?? params.per_page ?? 20));
  const total = Math.max(0, Number(metaRaw?.total ?? data.length));
  const computedLastPage = total === 0 ? 1 : Math.ceil(total / perPage);
  const lastPage = Math.max(1, Number(metaRaw?.last_page ?? computedLastPage));
  const meta: VehicleModelListMeta = {
    page: Math.max(1, Number(metaRaw?.page ?? params.page ?? 1)),
    per_page: perPage,
    total,
    last_page: lastPage,
  };
  return { data, meta };
}

async function fetchAllPages<T>(
  path: string,
  params: Record<string, string | number | undefined>,
): Promise<T[]> {
  const perPage = 100;
  let page = 1;
  const all: T[] = [];
  while (page < 1000) {
    const res = await api.get(path, { params: { ...params, page, per_page: perPage } });
    const body = res.data as Record<string, unknown>;
    const rows = extractDataRows<T>(body);
    const meta = body.meta as { total?: number; per_page?: number } | undefined;
    all.push(...rows);
    const total = typeof meta?.total === "number" ? meta.total : rows.length;
    const serverPerPage = typeof meta?.per_page === "number" ? meta.per_page : perPage;
    if (all.length >= total || rows.length < serverPerPage) break;
    page += 1;
  }
  return all;
}

export async function fetchAllVehicleModels(params: { q?: string; brand_id?: string } = {}): Promise<VehicleModelAdmin[]> {
  return fetchAllPages<VehicleModelAdmin>("/api/v1/catalog/vehicle-models", {
    q: params.q?.trim() || undefined,
    brand_id: params.brand_id?.trim() || undefined,
  });
}

/** One page of vehicle versions (server-side pagination). */
export async function fetchVehicleVersionsPage(params: {
  page?: number;
  per_page?: number;
  q?: string;
  brand_id?: string;
  vehicle_model_id?: string;
  name?: string;
  slug?: string;
  sort?: string;
  created_on?: string;
  created_from?: string;
  created_to?: string;
}): Promise<{ data: VehicleVersionAdmin[]; meta: VehicleVersionListMeta }> {
  const res = await api.get("/api/v1/catalog/vehicle-versions", {
    params: {
      page: params.page ?? 1,
      per_page: params.per_page ?? 20,
      q: params.q?.trim() || undefined,
      brand_id: params.brand_id?.trim() || undefined,
      vehicle_model_id: params.vehicle_model_id?.trim() || undefined,
      name: params.name?.trim() || undefined,
      slug: params.slug?.trim() || undefined,
      sort: params.sort?.trim() || undefined,
      created_on: params.created_on?.trim() || undefined,
      created_from: params.created_from?.trim() || undefined,
      created_to: params.created_to?.trim() || undefined,
    },
  });
  const body = res.data as Record<string, unknown>;
  const data = extractDataRows<VehicleVersionAdmin>(body);
  const metaRaw = body.meta as Record<string, unknown> | undefined;
  const perPage = Math.max(1, Number(metaRaw?.per_page ?? params.per_page ?? 20));
  const total = Math.max(0, Number(metaRaw?.total ?? data.length));
  const computedLastPage = total === 0 ? 1 : Math.ceil(total / perPage);
  const lastPage = Math.max(1, Number(metaRaw?.last_page ?? computedLastPage));
  const meta: VehicleVersionListMeta = {
    page: Math.max(1, Number(metaRaw?.page ?? params.page ?? 1)),
    per_page: perPage,
    total,
    last_page: lastPage,
  };
  return { data, meta };
}

export async function fetchAllVehicleVersions(
  params: { q?: string; brand_id?: string; vehicle_model_id?: string } = {},
): Promise<VehicleVersionAdmin[]> {
  return fetchAllPages<VehicleVersionAdmin>("/api/v1/catalog/vehicle-versions", {
    q: params.q?.trim() || undefined,
    brand_id: params.brand_id?.trim() || undefined,
    vehicle_model_id: params.vehicle_model_id?.trim() || undefined,
  });
}

export async function createVehicleModel(payload: {
  brand_id: number;
  name: string;
  slug: string;
  is_popular: boolean;
  sort_order: number;
}): Promise<VehicleModelAdmin> {
  const res = await api.post<{ data: VehicleModelAdmin }>("/api/v1/catalog/vehicle-models", payload);
  if (!res.data?.data) throw new Error("Invalid create vehicle model response.");
  return res.data.data;
}

export async function updateVehicleModel(
  id: number,
  payload: Partial<{
    brand_id: number;
    name: string;
    slug: string;
    is_popular: boolean;
    sort_order: number;
  }>,
): Promise<VehicleModelAdmin> {
  const res = await api.put<{ data: VehicleModelAdmin }>(`/api/v1/catalog/vehicle-models/${id}`, payload);
  if (!res.data?.data) throw new Error("Invalid update vehicle model response.");
  return res.data.data;
}

export async function deleteVehicleModel(id: number): Promise<void> {
  await api.delete(`/api/v1/catalog/vehicle-models/${id}`);
}

export async function createVehicleVersion(payload: {
  vehicle_model_id: number;
  name: string;
  slug: string;
  is_popular: boolean;
  sort_order: number;
  start_year?: number | null;
  end_year?: number | null;
  is_imported?: boolean;
}): Promise<VehicleVersionAdmin> {
  const res = await api.post<{ data: VehicleVersionAdmin }>("/api/v1/catalog/vehicle-versions", payload);
  if (!res.data?.data) throw new Error("Invalid create vehicle version response.");
  return res.data.data;
}

export async function updateVehicleVersion(
  id: number,
  payload: Partial<{
    vehicle_model_id: number;
    name: string;
    slug: string;
    is_popular: boolean;
    sort_order: number;
    start_year: number | null;
    end_year: number | null;
    is_imported: boolean;
  }>,
): Promise<VehicleVersionAdmin> {
  const res = await api.put<{ data: VehicleVersionAdmin }>(`/api/v1/catalog/vehicle-versions/${id}`, payload);
  if (!res.data?.data) throw new Error("Invalid update vehicle version response.");
  return res.data.data;
}

export async function deleteVehicleVersion(id: number): Promise<void> {
  await api.delete(`/api/v1/catalog/vehicle-versions/${id}`);
}
